'use client';

import { useState } from 'react';
import { toast } from 'sonner';
import { 
  exportGenerators, 
  downloadFile, 
  copyToClipboard, 
  exportFormats,
  type EpisodeData 
} from '@/utils/exportGenerators';

export const useExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [currentFormat, setCurrentFormat] = useState<string | null>(null);

  const transformEpisodeData = (content: any, cleanReadData?: any): EpisodeData => {
    console.log('🔄 Transforming episode data for export:', {
      contentExists: !!content,
      cleanReadDataExists: !!cleanReadData,
      contentKeys: content ? Object.keys(content) : [],
      cleanReadDataKeys: cleanReadData ? Object.keys(cleanReadData) : []
    });

    // Get the actual episode content
    const episodeContent = content?.content || content;
    
    const episodeData: EpisodeData = {
      title: episodeContent?.title || 'Untitled Episode',
      author: episodeContent?.author || 'Unknown Author',
      description: episodeContent?.description || cleanReadData?.videoInfo?.description,
      url: typeof window !== 'undefined' ? window.location.href : '',
      publishDate: episodeContent?.publishDate,
      duration: episodeContent?.duration || 0,
    };

    // Add summary if available
    if (cleanReadData?.blurb) {
      episodeData.summary = cleanReadData.blurb;
    }

    // Add chapters if available
    if (cleanReadData?.breakdown && Array.isArray(cleanReadData.breakdown)) {
      episodeData.chapters = cleanReadData.breakdown.map((chapter: any, index: number) => ({
        title: chapter.chapterTitle || `Chapter ${index + 1}`,
        startTime: chapter.timestamp || 0,
        endTime: index < cleanReadData.breakdown.length - 1 
          ? cleanReadData.breakdown[index + 1].timestamp || 0 
          : episodeData.duration,
        content: chapter.content
      }));
    }

    // Add key points if available
    if (cleanReadData?.breakdown && Array.isArray(cleanReadData.breakdown)) {
      const allKeyPoints: string[] = [];
      cleanReadData.breakdown.forEach((chapter: any) => {
        if (chapter.keyPoints && Array.isArray(chapter.keyPoints)) {
          allKeyPoints.push(...chapter.keyPoints);
        }
      });
      if (allKeyPoints.length > 0) {
        episodeData.keyPoints = allKeyPoints;
      }
    }

    // Add transcript if available
    if (content?.content?.transcript && Array.isArray(content.content.transcript)) {
      episodeData.transcript = content.content.transcript.map((segment: any) => ({
        text: segment.text || '',
        startTime: segment.timestamp?.[0] || segment.startTime || 0,
        endTime: segment.timestamp?.[1] || segment.endTime || 0,
        timestamp: segment.timestamp
      }));
    }

    console.log('✅ Episode data transformed:', episodeData);
    return episodeData;
  };

  const exportToFormat = async (
    formatId: string, 
    content: any, 
    cleanReadData?: any
  ): Promise<boolean> => {
    setIsExporting(true);
    setCurrentFormat(formatId);

    try {
      console.log(`📤 Starting export to ${formatId}...`);
      
      const episodeData = transformEpisodeData(content, cleanReadData);
      const generator = exportGenerators[formatId as keyof typeof exportGenerators];
      
      if (!generator) {
        throw new Error(`Export format "${formatId}" not supported`);
      }

      const exportedContent = await generator(episodeData);
      const format = exportFormats.find(f => f.id === formatId);
      
      if (!format) {
        throw new Error(`Format configuration for "${formatId}" not found`);
      }

      // Generate filename
      const safeTitle = episodeData.title
        .replace(/[^a-z0-9]/gi, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .substring(0, 50);
      
      const filename = `${safeTitle}.${format.fileExtension}`;

      // Download the file
      downloadFile(exportedContent, filename, format.mimeType);
      
      toast.success(`Successfully exported to ${format.name}`);
      console.log(`✅ Export to ${formatId} completed`);
      return true;

    } catch (error) {
      console.error(`❌ Export to ${formatId} failed:`, error);
      toast.error(`Failed to export to ${formatId}. Please try again.`);
      return false;
    } finally {
      setIsExporting(false);
      setCurrentFormat(null);
    }
  };

  const copyAsText = async (
    content: any,
    cleanReadData?: any,
    format: 'markdown' | 'text' = 'text'
  ): Promise<boolean> => {
    setIsExporting(true);
    
    try {
      console.log(`📋 Copying as ${format}...`);
      
      const episodeData = transformEpisodeData(content, cleanReadData);
      const generator = exportGenerators[format];
      const exportedContent = await generator(episodeData);
      
      const success = await copyToClipboard(exportedContent);
      
      if (success) {
        toast.success(`Copied as ${format} to clipboard`);
        console.log(`✅ Copy as ${format} completed`);
        return true;
      } else {
        throw new Error('Failed to copy to clipboard');
      }

    } catch (error) {
      console.error(`❌ Copy as ${format} failed:`, error);
      toast.error(`Failed to copy to clipboard. Please try again.`);
      return false;
    } finally {
      setIsExporting(false);
    }
  };

  const shareUrl = async (): Promise<boolean> => {
    try {
      const url = window.location.href;
      const success = await copyToClipboard(url);
      
      if (success) {
        toast.success('Episode URL copied to clipboard');
        return true;
      } else {
        throw new Error('Failed to copy URL');
      }
    } catch (error) {
      console.error('❌ Share URL failed:', error);
      toast.error('Failed to copy URL. Please try again.');
      return false;
    }
  };

  const exportToEmail = (content: any, cleanReadData?: any): void => {
    try {
      const episodeData = transformEpisodeData(content, cleanReadData);
      
      const subject = encodeURIComponent(`Check out: ${episodeData.title}`);
      const body = encodeURIComponent(
        `I thought you might be interested in this episode:\n\n` +
        `${episodeData.title}\n` +
        `By ${episodeData.author}\n\n` +
        `${episodeData.summary || 'A great podcast episode worth listening to!'}\n\n` +
        `Listen here: ${episodeData.url}`
      );
      
      const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
      window.open(mailtoUrl);
      
      toast.success('Email draft opened');
    } catch (error) {
      console.error('❌ Email export failed:', error);
      toast.error('Failed to open email. Please try again.');
    }
  };

  return {
    // State
    isExporting,
    currentFormat,
    
    // Export functions
    exportToFormat,
    copyAsText,
    shareUrl,
    exportToEmail,
    
    // Available formats
    availableFormats: exportFormats,
    
    // Utility
    transformEpisodeData,
  };
};